// frontend/src/lib/naming.ts
//
// Names used by more than one page: page names, one-line subtitles, product
// nouns, stage names, the footer note and browser titles. Renames happen here
// and nowhere else (plan 01 section 9.3).
//
// Plain UK English, no em-dashes. On screen the product and persona noun is
// "AI Procurement Coworker" (decision D15, plan 03 section 3), short form
// "coworker"; the global chat is the "AI Manager". The older "AI Employee"
// noun is kept as AI_EMPLOYEE_NOUN for the five-stage build and old text.

export const PRODUCT_NAME = 'Tender Command Center'
export const PRODUCT_SUBTITLE = 'Tender Command Center'
export const BRAND_NAME = 'Supervity'

export const FOOTER_NOTE =
  'Illustrative figures. Organisations and suppliers are fictitious.'

export const AI_MANAGER_NAME = 'AI Manager'
/** The noun on every card, kicker and actor line: "HANNAH · AI PROCUREMENT COWORKER · DISCOVER". */
export const PRODUCT_NOUN = 'AI Procurement Coworker'
/** The plural, for the hub's top node and the Explorer subtitle. */
export const PRODUCT_NOUN_PLURAL = 'AI Procurement Coworkers'
/** The five-stage build's noun. Kept so a rollback is a one-line switch. */
export const AI_EMPLOYEE_NOUN = 'AI Employee'
export const COWORKER_NOUN = 'coworker'

/** The six stages, in order. Backend stage labels never reach a first view. */
export const STAGE_NAMES = {
  discover: 'Discover',
  define: 'Define',
  source: 'Source',
  evaluate: 'Evaluate',
  negotiate: 'Negotiate',
  manage: 'Manage',
} as const

/** The five stages of the build before the six-stage lifecycle. Kept beside the six. */
export const STAGE_NAMES_FIVE = {
  define: 'Define',
  procure: 'Procure',
  evaluate: 'Evaluate',
  award: 'Award',
  manage: 'Manage',
} as const

export type StageName = keyof typeof STAGE_NAMES
export type StageNameFive = keyof typeof STAGE_NAMES_FIVE

/**
 * The baseline phrase a figure that claims time given back must carry in the
 * same tile, sentence or hover (plan 13 W1). The server sends the wording; this
 * only picks it. It never composes "agreed" itself, so a withdrawn or missing
 * baseline returns null and the figure shows no claim.
 */
export function baselinePhrase(
  value: { baseline?: { short?: string | null; long?: string | null } | null } | null | undefined,
  form: 'short' | 'long' = 'short'
): string | null {
  const text = value?.baseline?.[form]?.trim()
  return text ? text : null
}

/** Root of every breadcrumb. */
export const HOME_CRUMB = 'Command Briefing'

export interface PageName {
  /** Label in the rail, the breadcrumb and the browser title. */
  name: string
  /** One line under a list-page title, middle dots between parts. */
  subtitle?: string
}

/** Page names by route. A prefix match is used for nested routes. */
export const PAGE_NAMES: Record<string, PageName> = {
  '/': { name: 'Command Briefing' },
  '/tender': {
    name: 'Tender Explorer',
    subtitle:
      'North Thames University Hospitals · Strategic Procurement · AI Procurement Coworkers',
  },
  '/dashboard': { name: 'Dashboard and MI' },
  '/procurement/approvals': { name: 'My Approvals' },
  '/contracts': { name: 'Contract Hub' },
  '/contracts/templates': { name: 'Templates & Clauses' },
  '/supply-chain': {
    name: 'Supply Chain Desk',
    subtitle:
      "Sophie answers ward staff's questions about orders and deliveries, and brings you the ones that need a person.",
  },
  '/supply-chain/orders': { name: 'Orders and Deliveries' },
  '/ai/policies': { name: 'AI Policies' },
  '/ai/insights': { name: 'AI Insights' },
  '/agent-hub': { name: 'Coworker Hub' },
  '/data-manager': { name: 'Data Manager' },
  '/settings': { name: 'Settings' },
  '/admin/users': { name: 'Users and roles' },
  '/admin/roles': { name: 'Roles' },
  '/admin/groups': { name: 'Groups' },
  '/admin/sessions': { name: 'Sessions and sign-ins' },
  '/admin/events': { name: 'Sign-in events' },
  '/admin/audit': { name: 'Audit logs' },
  '/admin/settings': { name: 'Admin settings' },
  '/admin/start-process': { name: 'Start new process' },
  '/workbench': { name: 'Workbench' },
  '/vendors': { name: 'Vendors' },
  '/invoices': { name: 'Invoices' },
  '/brand': { name: 'Brand and design' },
  '/procurement': { name: 'Purchase Explorer' },
}

/** Page name for a route: exact match, then the longest matching prefix. */
export function pageNameFor(pathname: string): PageName | undefined {
  const clean = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname
  if (PAGE_NAMES[clean]) return PAGE_NAMES[clean]
  const prefix = Object.keys(PAGE_NAMES)
    .filter((route) => route !== '/' && clean.startsWith(`${route}/`))
    .sort((a, b) => b.length - a.length)[0]
  return prefix ? PAGE_NAMES[prefix] : undefined
}

/** "{Page} · Tender Command Center" for the browser tab. */
export function browserTitle(pageName?: string): string {
  return pageName ? `${pageName} · ${PRODUCT_NAME}` : `${PRODUCT_NAME} | ${BRAND_NAME}`
}
